import { useEffect, useRef, useState } from 'react'
import { buildAppSyncWsClient, WEBSOCKET_SUB_QUERY } from 'lib/appSyncSubscription'
import { EventOverlay, OverlayScene } from 'types/api'
import logger from 'logger'

const log = logger.extend('overlay-subscription')

export interface OverlayMessage<TElement> {
  kind: 'POLL' | 'TUG' | 'BOARD' | 'CAMERA_CONTROL' | 'GIVEAWAY' | 'TRIVIA'
  slug: string
  element?: TElement & { version: number }
  elementId?: string
  deleted: boolean
}

export interface ChannelEventCallbacks<TElement> {
  onUpdate: (element: TElement & { version: number }) => void
  onDelete: (elementId: string) => void
  onConnected?: () => void
}

export interface ChannelEventsState {
  connected: boolean
  error: Error | null
}

const idFromElement = (el: any): string | undefined =>
  el?.pollId ?? el?.tugId ?? el?.boardId ?? el?.camControlId ?? el?.giveawayId ?? el?.triviaId

export const useChannelEvents = <TElement>(
  channel: string | null,
  token: string | null,
  callbacks: ChannelEventCallbacks<TElement>
): ChannelEventsState => {
  const [state, setState] = useState<ChannelEventsState>({
    connected: false,
    error: null
  })
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks
  const lastSeenVersionRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    if (channel == null || token == null) return
    lastSeenVersionRef.current = new Map()
    setState({ connected: false, error: null })

    const client = buildAppSyncWsClient(token)

    const unsubscribe = client.subscribe(
      {
        query: WEBSOCKET_SUB_QUERY,
        variables: { channel }
      },
      {
        next: (data) => {
          const dataField = (data)?.WebsocketSub?.data
          if (dataField == null) return
          let envelope: OverlayMessage<TElement>
          try {
            envelope = typeof dataField === 'string'
              ? JSON.parse(dataField)
              : dataField
          } catch (err) {
            log({ parseEnvelope: 'failed', err: err instanceof Error ? err.name : 'unknown' })
            return
          }

          if (envelope.deleted && envelope.elementId != null && envelope.elementId !== '') {
            lastSeenVersionRef.current.delete(envelope.elementId)
            callbacksRef.current.onDelete(envelope.elementId)
            return
          }

          const incoming = envelope.element
          if (incoming == null) return
          const elementId = idFromElement(incoming)
          if (elementId == null || elementId === '') return

          const last = lastSeenVersionRef.current.get(elementId) ?? -1
          if (incoming.version <= last) {
            return
          }
          lastSeenVersionRef.current.set(elementId, incoming.version)
          callbacksRef.current.onUpdate(incoming)
        },
        error: (err) => {
          setState({
            connected: false,
            error: err instanceof Error ? err : new Error(String(err))
          })
        },
        complete: () => {
          setState((prev) => ({ ...prev, connected: false }))
        }
      }
    )

    const offConnected = client.on('connected', () => {
      setState({ connected: true, error: null })
      callbacksRef.current.onConnected?.()
    })
    const offClosed = client.on('closed', () => {
      setState((prev) => ({ ...prev, connected: false }))
    })

    return () => {
      unsubscribe()
      offConnected()
      offClosed()
      void client.dispose()
    }
  }, [channel, token])

  return state
}

export interface BotRevokedEnvelope {
  kind: 'BOT_REVOKED'
  slug: string
  subscriptionId: string
  channelLogin: string
  botUserId: string
  reason: string
  revokedAt: number
}

export type SystemEventEnvelope = BotRevokedEnvelope

export interface SystemEventCallbacks {
  onBotRevoked: (event: BotRevokedEnvelope) => void
}

export const useSystemEvents = (
  channel: string | null,
  token: string | null,
  callbacks: SystemEventCallbacks
): ChannelEventsState => {
  const [state, setState] = useState<ChannelEventsState>({
    connected: false,
    error: null
  })
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  useEffect(() => {
    if (channel == null || token == null) return
    setState({ connected: false, error: null })

    const client = buildAppSyncWsClient(token)

    const unsubscribe = client.subscribe(
      {
        query: WEBSOCKET_SUB_QUERY,
        variables: { channel }
      },
      {
        next: (data) => {
          const dataField = (data)?.WebsocketSub?.data
          if (dataField == null) return
          let envelope: SystemEventEnvelope
          try {
            envelope = typeof dataField === 'string'
              ? JSON.parse(dataField)
              : dataField
          } catch (err) {
            log({ parseEnvelope: 'failed', err: err instanceof Error ? err.name : 'unknown' })
            return
          }
          if (envelope?.kind === 'BOT_REVOKED') {
            callbacksRef.current.onBotRevoked(envelope)
          }
        },
        error: (err) => {
          setState({
            connected: false,
            error: err instanceof Error ? err : new Error(String(err))
          })
        },
        complete: () => {
          setState((prev) => ({ ...prev, connected: false }))
        }
      }
    )

    const offConnected = client.on('connected', () => {
      setState({ connected: true, error: null })
    })
    const offClosed = client.on('closed', () => {
      setState((prev) => ({ ...prev, connected: false }))
    })

    return () => {
      unsubscribe()
      offConnected()
      offClosed()
      void client.dispose()
    }
  }, [channel, token])

  return state
}

export interface OverlayStructureEnvelope {
  kind: 'OVERLAY'
  overlayId: string
  overlayKind: 'DESKTOP' | 'MOBILE'
  slug: string
  overlay: Omit<EventOverlay, 'scenes'>
  scene: OverlayScene | null
  deletedSceneId: string | null
  transition?: 'CUT' | 'FADE'
}

export interface OverlayStructureCallbacks {
  onOverlay: (env: OverlayStructureEnvelope) => void
  onConnected?: () => void
}

export const useOverlayStructureEvents = (
  channel: string | null,
  token: string | null,
  overlayId: string,
  callbacks: OverlayStructureCallbacks
): ChannelEventsState => {
  const [state, setState] = useState<ChannelEventsState>({
    connected: false,
    error: null
  })
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  useEffect(() => {
    if (channel == null || token == null) return
    setState({ connected: false, error: null })

    const client = buildAppSyncWsClient(token)

    const unsubscribe = client.subscribe(
      {
        query: WEBSOCKET_SUB_QUERY,
        variables: { channel }
      },
      {
        next: (data) => {
          const dataField = (data)?.WebsocketSub?.data
          if (dataField == null) return
          let envelope: OverlayStructureEnvelope
          try {
            envelope = typeof dataField === 'string'
              ? JSON.parse(dataField)
              : dataField
          } catch (err) {
            log({ parseEnvelope: 'failed', err: err instanceof Error ? err.name : 'unknown' })
            return
          }
          if (envelope?.kind === 'OVERLAY') {
            if (envelope.overlayId !== overlayId) return
            callbacksRef.current.onOverlay(envelope)
          }
        },
        error: (err) => {
          setState({
            connected: false,
            error: err instanceof Error ? err : new Error(String(err))
          })
        },
        complete: () => {
          setState((prev) => ({ ...prev, connected: false }))
        }
      }
    )

    const offConnected = client.on('connected', () => {
      setState({ connected: true, error: null })
      callbacksRef.current.onConnected?.()
    })
    const offClosed = client.on('closed', () => {
      setState((prev) => ({ ...prev, connected: false }))
    })

    return () => {
      unsubscribe()
      offConnected()
      offClosed()
      void client.dispose()
    }
  }, [channel, token, overlayId])

  return state
}

export interface CameraCommandEnvelope {
  elementId: string
  version: number
  seq: number
  command: string
  commandIndex: number
  issuedAt: number
  showAt: number
  ttlMs: number
  streak: number
  origin: string
  status?: string
}

export interface CameraCommandCallbacks {
  onCommand: (env: CameraCommandEnvelope) => void
  onConnected?: () => void
}

export const useCameraCommandEvents = (
  channel: string | null,
  token: string | null,
  camControlId: string,
  callbacks: CameraCommandCallbacks
): ChannelEventsState => {
  const [state, setState] = useState<ChannelEventsState>({
    connected: false,
    error: null
  })
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  useEffect(() => {
    if (channel == null || token == null) return
    setState({ connected: false, error: null })

    const client = buildAppSyncWsClient(token)

    const unsubscribe = client.subscribe(
      {
        query: WEBSOCKET_SUB_QUERY,
        variables: { channel }
      },
      {
        next: (data) => {
          const dataField = (data)?.WebsocketSub?.data
          if (dataField == null) return
          let envelope: CameraCommandEnvelope
          try {
            envelope = typeof dataField === 'string'
              ? JSON.parse(dataField)
              : dataField
          } catch (err) {
            log({ parseEnvelope: 'failed', err: err instanceof Error ? err.name : 'unknown' })
            return
          }
          if (typeof envelope?.elementId !== 'string' || typeof envelope?.version !== 'number') return
          if (envelope.elementId !== camControlId) return
          callbacksRef.current.onCommand(envelope)
        },
        error: (err) => {
          setState({
            connected: false,
            error: err instanceof Error ? err : new Error(String(err))
          })
        },
        complete: () => {
          setState((prev) => ({ ...prev, connected: false }))
        }
      }
    )

    const offConnected = client.on('connected', () => {
      setState({ connected: true, error: null })
      callbacksRef.current.onConnected?.()
    })
    const offClosed = client.on('closed', () => {
      setState((prev) => ({ ...prev, connected: false }))
    })

    return () => {
      unsubscribe()
      offConnected()
      offClosed()
      void client.dispose()
    }
  }, [channel, token, camControlId])

  return state
}
